import { useCallback, useEffect, useRef, useState } from "react";
import { FileSignature, PackageOpen, MapPin, KeyRound, Plus, Lock, Crosshair, Camera, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

/* Ghostwire v2 — contract board + dead-drop coordination.
   Contracts are escrowed at posting (no fake jobs), expire in 48h, and settle when
   the poster plants a drop, the contractor claims the code in-world, and escrow
   releases. Names never appear — aliases only. */

const CATS = [["courier", "Courier"], ["collection", "Collection"], ["muscle", "Muscle"], ["lookup", "Lookup"], ["recovery", "Recovery"], ["bounty", "Bounty — plate hit"]];

const DEMO = {
  posted: [
    { id: "c1", title: "Package to Pale Row", brief: "Small box. Don't shake it, don't open it.", category: "courier", category_label: "Courier", payout: 1200, poster_alias: "anon-4f2a", status: "posted" },
    { id: "c2", title: "Eyes on a sentinel", brief: "Who visits unit 31 after midnight. Plates only.", category: "lookup", category_label: "Lookup", payout: 800, poster_alias: "anon-b91c", status: "posted" },
  ],
  mine: [
    { id: "c3", title: "Recovery — the amber case", brief: "You know the one.", category: "recovery", category_label: "Recovery", payout: 3500, poster_alias: "You", status: "dropped", poster_pc: 99999, contractor_pc: 99101,
      drop: { code: "GX-A1B2C3", spot: "Foundry Ave phone box", label: "Amber case", claimed: false } },
  ],
  me: 99999,
};

export default function GhostwireContracts({ demo, serverId, token, accent }) {
  const [view, setView] = useState("board"); // board | mine | post | claim
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({ title: "", brief: "", category: "courier", payout: 500, target_plate: "" });
  const [dropLabel, setDropLabel] = useState({});
  const [claimCode, setClaimCode] = useState("");
  const proofRef = useRef(null);
  const [proofFor, setProofFor] = useState(null);

  const load = useCallback(async () => {
    if (demo) { setData(DEMO); return; }
    try {
      const r = await api.get(`/nc/servers/${serverId}/ghostwire/board`, { params: { token } });
      setData(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, [demo, serverId, token]);
  useEffect(() => { load(); }, [load]);

  const act = async (key, fn) => {
    setBusy(key);
    try {
      const r = await fn();
      toast.success(r.data.message);
      if (r.data.drop) toast.success(`Drop code ${r.data.drop.code} — ${r.data.drop.spot}`);
      setView("mine");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const submitProof = async (contractId, file) => {
    if (!file) return;
    setBusy(contractId + "proof");
    try {
      if (demo) { toast.success("Proof submitted (demo)"); return; }
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/nc/servers/${serverId}/ghostwire/contracts/${contractId}/proof?token=${token}`, fd);
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); setProofFor(null); }
  };

  if (!data) return <div className="p-6 font-mono text-[10px] text-white/40 uppercase tracking-[0.25em] animate-pulse">Entering the wire…</div>;

  const mePc = data.me;

  const vBtn = (id, label) => (
    <button key={id} data-testid={`gw-view-${id}`} onClick={() => setView(id)}
      className={`flex-1 py-1.5 font-mono text-[8px] font-bold uppercase tracking-[0.18em] border transition-colors ${view === id ? "text-white" : "text-white/40 border-white/10"}`}
      style={view === id ? { borderColor: `${accent}66`, backgroundColor: `${accent}18` } : {}}>{label}</button>
  );

  const statusChip = (s) => (
    <span className={`font-mono text-[7px] uppercase tracking-[0.15em] px-1.5 py-0.5 border ${s === "released" ? "border-emerald-400/40 text-emerald-300" : s === "delivered" ? "border-sky-400/40 text-sky-300" : s === "dropped" ? "border-amber-400/40 text-amber-300" : s === "accepted" ? "border-white/25 text-white/60" : "border-white/15 text-white/40"}`}>{s}</span>
  );

  return (
    <div data-testid="gw-contracts" className="p-3 flex flex-col gap-2">
      <div className="flex gap-1">{vBtn("board", "Board")}{vBtn("mine", "Mine")}{vBtn("post", "Post")}{vBtn("claim", "Claim")}</div>

      {view === "board" && (
        <>
          <p className="font-mono text-[8px] text-white/40 leading-relaxed px-1">Escrow locked at posting. Work happens off-grid. Aliases only.</p>
          {data.posted.length === 0 && <p data-testid="gw-board-empty" className="font-mono text-[10px] text-white/40 text-center mt-8 border border-dashed border-white/10 p-4 rounded-xl">Board's quiet. Too quiet.</p>}
          {data.posted.map((k) => (
            <div key={k.id} data-testid={`gw-posted-${k.id}`} className="rounded-2xl bg-white/5 border border-white/10 p-3">
              <div className="flex items-center gap-2">
                <FileSignature className="w-3.5 h-3.5 text-sky-300 shrink-0" />
                <p className="font-sans font-semibold text-[12px] text-white truncate">{k.title}</p>
                <span className="ml-auto font-mono text-[10px] font-bold text-emerald-300">${k.payout.toLocaleString()}</span>
              </div>
              {k.category === "bounty" && k.target_plate && (
                <p className="mt-1.5 font-mono text-[11px] font-bold tracking-[0.3em] text-red-300 border border-red-400/30 bg-red-400/5 rounded-lg px-2 py-1 inline-flex items-center gap-1.5">
                  <Crosshair className="w-3 h-3" /> {k.target_plate}
                </p>
              )}
              <p className="font-mono text-[9px] text-white/50 mt-1 leading-relaxed">{k.brief}</p>
              <p className="font-mono text-[7px] text-white/30 mt-1 uppercase tracking-[0.15em]">{k.category_label} · {k.poster_alias}</p>
              <button data-testid={`gw-accept-${k.id}`} onClick={() => act(k.id, () => api.post(`/nc/servers/${serverId}/ghostwire/contracts/${k.id}/accept`, { token }))} disabled={!!busy}
                className="mt-2 w-full rounded-full py-2 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-black disabled:opacity-40 transition-opacity hover:opacity-85"
                style={{ backgroundColor: "#38bdf8" }}>Take the contract</button>
            </div>
          ))}
        </>
      )}

      {view === "mine" && (
        <>
          {data.mine.length === 0 && <p className="font-mono text-[10px] text-white/40 text-center mt-8 border border-dashed border-white/10 p-4 rounded-xl">No contracts with your prints on them</p>}
          {data.mine.map((k) => (
            <div key={k.id} data-testid={`gw-mine-${k.id}`} className="rounded-2xl bg-white/5 border border-white/10 p-3">
              <div className="flex items-center gap-2">
                <p className="font-sans font-semibold text-[12px] text-white truncate">{k.title}</p>
                <span className="ml-auto">{statusChip(k.status)}</span>
              </div>
              <p className="font-mono text-[8px] text-white/40 mt-1">${k.payout.toLocaleString()} · {k.category_label}{k.target_plate ? ` · plate ${k.target_plate}` : ""}</p>
              {k.proof && (
                <div data-testid={`gw-proof-${k.id}`} className="mt-2 rounded-xl border border-sky-400/25 bg-sky-400/5 p-2.5">
                  <p className="font-mono text-[8px] text-sky-300 uppercase tracking-[0.15em] flex items-center gap-1.5"><Camera className="w-3 h-3" /> Proof shot on the table</p>
                  <a href={k.proof.url} target="_blank" rel="noreferrer" className="block mt-1.5">
                    <img src={k.proof.url} alt="bounty proof" className="rounded-lg border border-white/10 max-h-32 object-cover w-full" />
                  </a>
                </div>
              )}
              {k.drop && (
                <div data-testid={`gw-drop-${k.id}`} className="mt-2 rounded-xl border border-amber-400/25 bg-amber-400/5 p-2.5">
                  <p className="font-mono text-[8px] text-amber-300 uppercase tracking-[0.15em] flex items-center gap-1.5"><PackageOpen className="w-3 h-3" /> Dead drop — {k.drop.claimed ? "claimed" : "live"}</p>
                  <p className="font-mono text-[9px] text-white/70 mt-1 flex items-center gap-1.5"><MapPin className="w-3 h-3 text-white/40" /> {k.drop.spot}</p>
                  <p className="font-mono text-[9px] text-white/70 mt-0.5 flex items-center gap-1.5"><KeyRound className="w-3 h-3 text-white/40" /> code <span className="font-bold text-amber-200">{k.drop.code}</span></p>
                  <p className="font-mono text-[7px] text-white/30 mt-0.5">{k.drop.label}</p>
                </div>
              )}
              <div className="mt-2 flex flex-col gap-1.5">
                {k.status === "accepted" && k.poster_pc === mePc && (
                  <p className="font-mono text-[8px] text-white/40">Awaiting the drop — plant it below.</p>
                )}
                {k.status === "accepted" && k.contractor_pc === mePc && k.category === "bounty" && (
                  <>
                    <input ref={proofRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                      onChange={(e) => submitProof(k.id, e.target.files?.[0])} />
                    <button data-testid={`gw-proof-btn-${k.id}`} onClick={() => { setProofFor(k.id); proofRef.current?.click(); }} disabled={!!busy}
                      className="rounded-full py-2 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-black disabled:opacity-40 flex items-center justify-center gap-1.5 transition-opacity hover:opacity-85"
                      style={{ backgroundColor: "#f43f5e" }}>
                      {busy === k.id + "proof" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />} Submit plate photo
                    </button>
                  </>
                )}
                {k.status === "accepted" && k.contractor_pc === mePc && k.category !== "bounty" && (
                  <p className="font-mono text-[8px] text-white/40">Waiting on the poster to plant the drop…</p>
                )}
                {k.status === "accepted" && k.poster_pc === mePc && (
                  <div className="flex gap-1.5">
                    <input data-testid={`gw-droplabel-${k.id}`} value={dropLabel[k.id] || ""} onChange={(e) => setDropLabel((s) => ({ ...s, [k.id]: e.target.value }))}
                      placeholder="What's in the drop" className="flex-1 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 font-mono text-[9px] text-white focus:outline-none focus:border-amber-400/50" />
                    <button data-testid={`gw-plant-${k.id}`} onClick={() => act(k.id + "p", () => api.post(`/nc/servers/${serverId}/ghostwire/contracts/${k.id}/drop`, { token, label: dropLabel[k.id] || "" }))}
                      disabled={!!busy || !(dropLabel[k.id] || "").trim()}
                      className="font-mono text-[8px] font-bold uppercase tracking-[0.15em] text-black rounded-full px-3 disabled:opacity-40"
                      style={{ backgroundColor: "#f59e0b" }}>Plant drop</button>
                  </div>
                )}
                {k.status === "proof_submitted" && (
                  <div className="flex gap-1.5">
                    <button data-testid={`gw-release-${k.id}`} onClick={() => act(k.id + "r", () => api.post(`/nc/servers/${serverId}/ghostwire/contracts/${k.id}/release`, { token, code: "----" }))}
                      disabled={!!busy}
                      className="flex-1 rounded-full py-2 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-black disabled:opacity-40 transition-opacity hover:opacity-85"
                      style={{ backgroundColor: "#4ade80" }}>Release ${k.payout.toLocaleString()}</button>
                    <button data-testid={`gw-reject-${k.id}`} onClick={() => act(k.id + "j", () => api.post(`/nc/servers/${serverId}/ghostwire/contracts/${k.id}/reject-proof`, { token, code: "----" }))}
                      disabled={!!busy}
                      className="rounded-full py-2 px-3 font-mono text-[8px] uppercase tracking-[0.15em] border border-red-400/40 text-red-300 hover:bg-red-400/10 disabled:opacity-40 transition-colors">
                      <XCircle className="w-3 h-3 inline mr-1" /> Reject
                    </button>
                  </div>
                )}
                {k.status === "delivered" && (
                  <button data-testid={`gw-release-${k.id}`} onClick={() => act(k.id + "r", () => api.post(`/nc/servers/${serverId}/ghostwire/contracts/${k.id}/release`, { token, code: "----" }))}
                    disabled={!!busy}
                    className="rounded-full py-2 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-black disabled:opacity-40 transition-opacity hover:opacity-85"
                    style={{ backgroundColor: "#4ade80" }}>Release ${k.payout.toLocaleString()} escrow</button>
                )}
                {k.status === "posted" && (
                  <button data-testid={`gw-cancel-${k.id}`} onClick={() => act(k.id + "c", () => api.post(`/nc/servers/${serverId}/ghostwire/contracts/${k.id}/cancel`, { token, code: "----" }))}
                    disabled={!!busy}
                    className="rounded-full py-1.5 font-mono text-[8px] uppercase tracking-[0.2em] border border-white/15 text-white/50 hover:text-white transition-colors">
                    Pull contract (5% board fee)
                  </button>
                )}
              </div>
            </div>
          ))}
        </>
      )}

      {view === "post" && (
        <div className="rounded-2xl bg-white/5 border border-white/10 p-3 flex flex-col gap-2">
          <p className="font-mono text-[8px] text-white/40 uppercase tracking-[0.2em] flex items-center gap-1.5"><Lock className="w-3 h-3" /> escrow locks at posting · 48h TTL</p>
          <input data-testid="gw-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={60}
            placeholder="Job title — vague is professional" className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 font-mono text-[10px] text-white focus:outline-none focus:border-sky-400/50" />
          <textarea data-testid="gw-brief" value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} maxLength={280} rows={3}
            placeholder="Brief. No names. No trace." className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 font-mono text-[10px] text-white focus:outline-none focus:border-sky-400/50 resize-none" />
          <div className="flex gap-1.5">
            <select data-testid="gw-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="flex-1 bg-black border border-white/10 rounded-xl px-2 py-2 font-mono text-[10px] text-white [color-scheme:dark]">
              {CATS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
            <input data-testid="gw-payout" value={form.payout} onChange={(e) => setForm({ ...form, payout: parseInt(e.target.value.replace(/\D/g, "") || "0", 10) })}
              className="w-24 bg-white/5 border border-white/10 rounded-xl px-3 py-2 font-mono text-[10px] text-white focus:outline-none focus:border-sky-400/50" placeholder="$" />
          </div>
          {form.category === "bounty" && (
            <input data-testid="gw-plate" value={form.target_plate} onChange={(e) => setForm({ ...form, target_plate: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 10) })}
              placeholder="TARGET PLATE — e.g. NCB-4242"
              className="bg-red-400/5 border border-red-400/30 rounded-xl px-3 py-2 font-mono text-[12px] font-bold tracking-[0.3em] text-red-200 text-center focus:outline-none focus:border-red-400/60" />
          )}
          <button data-testid="gw-post" onClick={() => act("post", () => api.post(`/nc/servers/${serverId}/ghostwire/contracts`, { token, ...form }))}
            disabled={!!busy || form.title.trim().length < 4 || form.brief.trim().length < 8 || form.payout < 100 || (form.category === "bounty" && form.target_plate.trim().length < 2)}
            className="rounded-full py-2.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-black disabled:opacity-40 flex items-center justify-center gap-1.5 transition-opacity hover:opacity-85"
            style={{ backgroundColor: "#38bdf8" }}>
            <Plus className="w-3 h-3" /> Post & escrow ${form.payout.toLocaleString()}
          </button>
        </div>
      )}

      {view === "claim" && (
        <div className="rounded-2xl bg-white/5 border border-white/10 p-3 flex flex-col gap-2">
          <p className="font-mono text-[8px] text-white/40 leading-relaxed">Standing at a drop? Punch the one-time code. Wrong codes three times and the drop burns.</p>
          <input data-testid="gw-claim-code" value={claimCode} onChange={(e) => setClaimCode(e.target.value.toUpperCase())} maxLength={12}
            placeholder="GX-······" className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 font-mono text-[13px] font-bold tracking-[0.3em] text-amber-200 text-center focus:outline-none focus:border-amber-400/50" />
          <button data-testid="gw-claim" onClick={() => act("claim", () => api.post(`/nc/servers/${serverId}/ghostwire/drops/claim`, { token, code: claimCode }))}
            disabled={!!busy || claimCode.trim().length < 6}
            className="rounded-full py-2.5 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-black disabled:opacity-40 transition-opacity hover:opacity-85"
            style={{ backgroundColor: "#f59e0b" }}>Claim the drop</button>
        </div>
      )}
    </div>
  );
}
